import type { ExtendedRecordMap } from "notion-types";

import { newElementWith } from "../../packages/excalidraw/element/mutateElement";
import { t } from "../../packages/excalidraw/i18n";
import type { ExcalidrawImperativeAPI } from "../../packages/excalidraw/types";
import type {
  ExcalidrawElement,
  ExcalidrawLinearElement,
  ExcalidrawMindmapNodeElement,
  ExcalidrawNotionElement,
  ExcalidrawTextElement,
} from "../../packages/excalidraw/element/types";

import {
  clearNotionRequestCache,
  notionDatabase,
  notionPageRecordMap,
} from "../services/notion";

import {
  buildMindmapSkeleton,
  buildMindmapTree,
} from "../../packages/mindmap-core/src/mindmapTree";
import type { MindmapTree } from "../../packages/mindmap-core/src/mindmapTree";
import {
  buildDbMindmapTree,
  candidateGroupings,
} from "../../packages/mindmap-core/src/mindmapDbTree";
import type {
  Grouping,
  NotionDbRow,
} from "../../packages/mindmap-core/src/mindmapDbTree";
import type {
  MindmapCustomData,
  MindmapIdentity,
  MindmapSource,
} from "../../packages/mindmap-core/src/mindmapElements";
import {
  buildMindmapNodeElement,
  buildMindmapConnectorElement,
  emitMindmapElements,
  pruneMindmapDeletionLedger,
  mindmapSuppressedBlockIds,
  withMindmapGroupingCandidates,
} from "../../packages/mindmap-core/src/mindmapElements";
import {
  assignBranchColors,
  MINDMAP_NEUTRAL_COLOR,
} from "../../packages/mindmap-core/src/mindmapColors";
import { mindmapConnectorGeometry } from "../../packages/mindmap-core/src/mindmapConnectorGeometry";
import { withMindmapBindings } from "../../packages/mindmap-core/src/mindmapBinding";
import {
  clearRefreshInFlight,
  markRefreshInFlight,
} from "./mindmapRefreshBusy";
import {
  V_GAP,
  H_GAP,
  layoutMindmap,
  mindmapNodeHeight,
  mindmapNodeWidth,
} from "../../packages/mindmap-core/src/mindmapLayout";
import { resolveMapCollisions, separateOverlappingMaps } from "./mindmapExpand";

/**
 * Diff engine for "Refresh from Notion" (issue #46).
 *
 * Given a freshly rebuilt tree and the on-canvas elements that carry a given
 * mapId, `diffMindmap` computes a minimal plan of adds / removes / renames that
 * reconciles the canvas with Notion under the agreed ownership rule: Notion owns
 * text + structure, the canvas owns position + style. So renames overwrite node
 * text, new blocks are inserted, and deleted blocks remove the generated node
 * plus its generated connector only — never a survivor's position/color, never a
 * user-drawn element (those never carry this mapId and are filtered out before
 * the diff sees them).
 *
 * The function is pure: same inputs → same plan, no scene mutation.
 */

interface MindmapRename {
  elementId: string;
  newText: string;
  /**
   * The box the renamed pill needs for its NEW label, centered on the old one.
   * Present only when the pill is still the size the layout gave it: Notion owns
   * text, so a longer title used to be poured into the old box and ellipsized
   * with no way back except dragging the pill bigger by hand. A pill the USER
   * resized carries no box here and keeps the size they chose.
   */
  box?: MindmapBox;
}

/** A node box in top-left scene coordinates (matches on-canvas elements). */
export interface MindmapBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * A new node to insert on refresh. Carries everything the apply step needs to
 * emit the node element and its connector without re-running global layout:
 * the fresh tree node (text/kind/pageId), a box placed near its siblings, the
 * parent's box (so the connector can anchor on its side edge), and the inherited
 * branch color.
 */
export interface MindmapAdd {
  blockId: string;
  parentBlockId: string;
  node: MindmapTree;
  box: MindmapBox;
  parentBox: MindmapBox;
  strokeColor?: string;
  /** The page whose recordMap this block lives in, so a later refresh re-walks
   * the right source. Absent → the apply step defaults it to the map root. */
  sourcePageId?: string;
}

/**
 * A survivor whose Notion parent changed. Structure is Notion-owned, so the node
 * keeps its element (and therefore its canvas-owned position and style) while
 * its `parentBlockId` is rewritten and its incoming connector is re-routed from
 * the new parent's box. Without this a move reads as remove + add: the user
 * loses the node's position, colour and any attachment to it.
 */
export interface MindmapReparent {
  elementId: string;
  blockId: string;
  newParentBlockId: string;
}

export interface MindmapPlan {
  adds: MindmapAdd[];
  removes: string[];
  renames: MindmapRename[];
  /**
   * Optional, and omitted entirely when empty: a no-op plan keeps its original
   * three-key shape, so callers that build or compare plans by hand stay valid.
   */
  reparents?: MindmapReparent[];
}

const mindmapOf = (el: ExcalidrawElement): MindmapCustomData | undefined =>
  (el.customData?.mindmap as MindmapCustomData | undefined) ?? undefined;

/** The branch colour a node element currently carries (V2 pills only). */
const branchColorOf = (el: ExcalidrawElement): string | undefined =>
  el.type === "mindmapNode"
    ? (el as ExcalidrawMindmapNodeElement).branchColor
    : undefined;

/**
 * The label a node element currently shows. V2 nodes are `mindmapNode` pills
 * (label); legacy maps may still hold `notion` (title) or `text` (text) nodes,
 * so all three are read for backward compatibility.
 */
const elementText = (el: ExcalidrawElement): string =>
  el.type === "mindmapNode"
    ? String((el as unknown as { label?: string }).label ?? "")
    : el.type === "notion"
    ? String((el as unknown as { title?: string }).title ?? "")
    : String((el as unknown as { text?: string }).text ?? "");

const boxOf = (el: ExcalidrawElement): MindmapBox => ({
  x: el.x,
  y: el.y,
  width: el.width,
  height: el.height,
});

/* ------------------------------------------------------------------------- *
 * Native text nodes: a `nodeKind: "text"` pill has no Notion block behind it,
 * so a refresh never removes one. When its Notion parent disappears, the text
 * node and its subtree (text all the way down — a text node cannot have Notion
 * children) move to the nearest surviving ancestor, the root as last resort.
 * ------------------------------------------------------------------------- */

const isTextNode = (el: ExcalidrawElement): boolean =>
  el.type === "mindmapNode" &&
  (el as ExcalidrawMindmapNodeElement).nodeKind === "text";

/**
 * Walk `parentBlockId` links from `blockId` up through the CURRENT nodes until
 * one `survives`; falls back to `rootBlockId`. A missing or cyclic chain also
 * lands on the root.
 */
const nearestSurvivingAncestor = (
  blockId: string,
  currentNodes: ReadonlyMap<string, ExcalidrawElement>,
  survives: (blockId: string) => boolean,
  rootBlockId: string,
): string => {
  const seen = new Set<string>();
  // A parent the user hand-deleted is not on canvas: the chain ends there.
  const parentOf = (id: string) => {
    const el = currentNodes.get(id);
    return el ? mindmapOf(el)?.parentBlockId : undefined;
  };
  let cursor = parentOf(blockId);
  while (cursor && !seen.has(cursor)) {
    if (survives(cursor)) {
      return cursor;
    }
    seen.add(cursor);
    cursor = parentOf(cursor);
  }
  return rootBlockId;
};

/** A live text node element back as a tree node, subtree included. */
const textTreeOf = (
  el: ExcalidrawElement,
  childrenByParent: ReadonlyMap<string, ExcalidrawElement[]>,
): MindmapTree => {
  const mm = mindmapOf(el)!;
  const pill = el as ExcalidrawMindmapNodeElement;
  const children = (childrenByParent.get(mm.blockId) ?? []).map((child) =>
    textTreeOf(child, childrenByParent),
  );
  return {
    blockId: mm.blockId,
    text: pill.label,
    kind: "text",
    hasChildren: children.length > 0,
    children,
    ...(pill.branchColor !== MINDMAP_NEUTRAL_COLOR
      ? { color: pill.branchColor }
      : {}),
    ...(mm.provenance ? { provenance: mm.provenance } : {}),
  };
};

/**
 * Re-attach the map's live text subtrees to a freshly built tree, each
 * top-level one under its recorded parent, else the nearest surviving
 * ancestor, else the root.
 */
export const graftTextNodes = (
  tree: MindmapTree,
  mine: readonly ExcalidrawElement[],
): MindmapTree => {
  const nodes = mine.filter(
    (el) => !el.isDeleted && mindmapOf(el)?.role === "node",
  );
  const textNodes = nodes.filter(isTextNode);
  if (textNodes.length === 0) {
    return tree;
  }
  const currentNodes = new Map(nodes.map((el) => [mindmapOf(el)!.blockId, el]));
  const childrenByParent = new Map<string, ExcalidrawElement[]>();
  for (const el of textNodes) {
    const parent = mindmapOf(el)!.parentBlockId;
    if (parent) {
      childrenByParent.set(parent, [
        ...(childrenByParent.get(parent) ?? []),
        el,
      ]);
    }
  }
  const freshIds = new Set<string>();
  const collect = (node: MindmapTree) => {
    freshIds.add(node.blockId);
    node.children.forEach(collect);
  };
  collect(tree);

  // Top-level text nodes, grouped by the fresh node they will hang from.
  const graftsByParent = new Map<string, MindmapTree[]>();
  for (const el of textNodes) {
    const mm = mindmapOf(el)!;
    const parent = mm.parentBlockId
      ? currentNodes.get(mm.parentBlockId)
      : undefined;
    if (parent && isTextNode(parent)) {
      continue;
    }
    const target = nearestSurvivingAncestor(
      mm.blockId,
      currentNodes,
      (blockId) => freshIds.has(blockId),
      tree.blockId,
    );
    graftsByParent.set(target, [
      ...(graftsByParent.get(target) ?? []),
      textTreeOf(el, childrenByParent),
    ]);
  }
  const attach = (node: MindmapTree): MindmapTree => {
    const grafts = graftsByParent.get(node.blockId) ?? [];
    const children = [...node.children.map(attach), ...grafts];
    return {
      ...node,
      children,
      ...(node.hasChildren !== undefined || grafts.length
        ? { hasChildren: children.length > 0 }
        : {}),
    };
  };
  return attach(tree);
};

interface FreshEntry {
  node: MindmapTree;
  parentBlockId: string | null;
}

/**
 * Pre-order walk of the fresh tree. Pre-order guarantees a parent is visited
 * before its children, so a whole new subtree is placed top-down (a new child
 * can anchor to a just-placed new parent). "+N more" stubs are walked like any
 * other node: their id is synthetic but deterministic (`${parentId}::more`), so
 * the diff reconciles them by blockId — a stub appears, its count renames, or it
 * disappears exactly as the branch's truncation changes.
 */
const walkFresh = (
  tree: MindmapTree,
  parentBlockId: string | null,
  out: FreshEntry[],
): void => {
  out.push({ node: tree, parentBlockId });
  for (const child of tree.children) {
    walkFresh(child, tree.blockId, out);
  }
};

/**
 * The distinct Notion source pages a map's on-canvas nodes came from: the root
 * page plus every page expanded in place (each expanded page's blocks carry its
 * id as `sourcePageId`). A node without a `sourcePageId` (root / legacy) counts
 * as the root. This is the set of pages a V2 refresh must re-walk (#52).
 */
export const mindmapSourceIds = (
  elements: readonly ExcalidrawElement[],
  rootPageId: string,
): string[] => {
  const out = new Set<string>();
  for (const el of elements) {
    const mm = mindmapOf(el);
    if (mm?.role === "node") {
      out.add(mm.sourcePageId ?? rootPageId);
    }
  }
  return [...out];
};

/** Every blockId that owns children in a base tree (its always-visible level). */
const collectInternalIds = (tree: MindmapTree, out: Set<string>): void => {
  if (tree.children.length > 0) {
    out.add(tree.blockId);
  }
  for (const child of tree.children) {
    collectInternalIds(child, out);
  }
};

/**
 * Build ONE fresh comparison tree spanning every re-walked source, sliced to the
 * frontier that is actually VISIBLE on canvas (#52). A node shows its fresh
 * children when either:
 *  - it is a structural always-visible level of some source's base tree — the
 *    root's level 1, or a database's group→row levels (so a brand-new group
 *    still brings its rows), OR
 *  - it currently has children on canvas (the user expanded it) — so an expanded
 *    subtree is re-walked, while a chip-collapsed node stays a leaf and never
 *    gains hidden children.
 *
 * At an expanded PAGE node the walk switches to that page's own source (its
 * skeleton spliced in), so each source is diffed at its own visible depth. A
 * page node whose source failed to fetch (in `failedSources`, or simply absent
 * from `fullTreeBySource`) is left a leaf — the caller drops that subtree from
 * the diff scope so it survives untouched.
 *
 * Returns the combined tree plus a blockId → sourcePageId map, so the diff can
 * stamp each freshly added node with the page its block really lives in.
 */
export const buildCombinedComparisonTree = (params: {
  mine: readonly ExcalidrawElement[];
  rootPageId: string;
  fullTreeBySource: ReadonlyMap<string, MindmapTree>;
  baseTreeBySource: ReadonlyMap<string, MindmapTree>;
  failedSources: ReadonlySet<string>;
}): { tree: MindmapTree; sourceByBlockId: Map<string, string> } => {
  const {
    mine,
    rootPageId,
    fullTreeBySource,
    baseTreeBySource,
    failedSources,
  } = params;

  const alwaysShown = new Set<string>();
  for (const base of baseTreeBySource.values()) {
    collectInternalIds(base, alwaysShown);
  }

  const hasCanvasChildren = new Set<string>();
  // Every blockId present on canvas. Legacy v1 maps (still in users'
  // localStorage) carry NO parentBlockId, so `hasCanvasChildren` is empty for
  // them and the frontier would slice a full-depth v1 map to root+L1, deleting
  // every deeper node on refresh. We recover the expanded frontier by matching a
  // node's FRESH children against the blockIds actually on canvas: a node counts
  // as expanded when any of its fresh children is present, so a v1 full-depth map
  // re-walks fully.
  const canvasBlockIds = new Set<string>();
  // Nodes flagged expanded on canvas. A node emptied by a prior refresh (every
  // child removed) keeps expanded===true but has no canvas children, so it must
  // be re-walked from its flag alone — otherwise it stays a permanent leaf and
  // can never regain its children (#M4).
  const expandedBlockIds = new Set<string>();
  for (const el of mine) {
    const mm = mindmapOf(el);
    if (mm?.role === "node") {
      canvasBlockIds.add(mm.blockId);
      if (mm.parentBlockId) {
        hasCanvasChildren.add(mm.parentBlockId);
      }
      if ((el as { expanded?: boolean }).expanded === true) {
        expandedBlockIds.add(mm.blockId);
      }
    }
  }

  const sourceByBlockId = new Map<string, string>();

  // blockIds already placed in the combined tree. A recordMap is not acyclic and
  // the tree walk deliberately lets one block appear in two branches (a synced
  // block, a transclusion, a page linked twice), and an expanded page's own tree
  // can re-surface a block the root's tree already carried. Every such repeat
  // used to be walked again, so the diff saw the SAME blockId as two distinct
  // fresh entries and planned two adds for it — the duplicate pill the owner
  // screenshotted, one copy stacked on the original and one pushed elsewhere by
  // the overlap avoidance. First occurrence wins; repeats become leaves.
  const emitted = new Set<string>();

  const build = (node: MindmapTree, source: string): MindmapTree | null => {
    if (emitted.has(node.blockId)) {
      return null;
    }
    emitted.add(node.blockId);
    sourceByBlockId.set(node.blockId, source);

    const showChildren =
      alwaysShown.has(node.blockId) ||
      hasCanvasChildren.has(node.blockId) ||
      expandedBlockIds.has(node.blockId) ||
      node.children.some((c) => canvasBlockIds.has(c.blockId));
    if (!showChildren) {
      return { ...node, children: [] };
    }

    // An expanded page node's children live in its OWN page, not this source.
    if (node.kind === "page" && node.pageId && node.pageId !== source) {
      const childTree = fullTreeBySource.get(node.pageId);
      if (!childTree || failedSources.has(node.pageId)) {
        return { ...node, children: [] };
      }
      return { ...node, children: buildAll(childTree.children, node.pageId!) };
    }

    return { ...node, children: buildAll(node.children, source) };
  };

  const buildAll = (
    children: readonly MindmapTree[],
    source: string,
  ): MindmapTree[] =>
    children
      .map((c) => build(c, source))
      .filter((c): c is MindmapTree => c !== null);

  const rootFull = fullTreeBySource.get(rootPageId);
  const tree =
    (rootFull && build(rootFull, rootPageId)) ??
    ({
      blockId: rootPageId,
      text: "",
      kind: "page" as const,
      children: [],
    } as MindmapTree);

  return { tree, sourceByBlockId };
};

export const diffMindmap = (
  freshTree: MindmapTree,
  currentElements: readonly ExcalidrawElement[],
  sourceByBlockId?: ReadonlyMap<string, string>,
  /**
   * blockIds the user has deleted (tombstoned) on this map. Notion still lists
   * them, so without this the next refresh keeps resurrecting them.
   */
  excludeBlockIds?: ReadonlySet<string>,
  /**
   * Boxes an added node must not land on even though they are OUTSIDE the diff
   * scope — a failed source's surviving subtree, most of all. Placement used to
   * collide only against the boxes it was diffing, so a refresh dropped fresh
   * pills straight on top of nodes it had deliberately excluded from the diff.
   */
  occupiedBoxes?: readonly MindmapBox[],
): MindmapPlan => {
  const freshEntries: FreshEntry[] = [];
  walkFresh(freshTree, null, freshEntries);
  const freshNodes = new Map(freshEntries.map((e) => [e.node.blockId, e.node]));

  const currentNodes = new Map<string, ExcalidrawElement>();
  // Generated connectors, keyed by the child blockId they point to, so a removed
  // node takes exactly its own connector with it. Stub nodes and their
  // connectors are included: a stub's connector shares the stub's blockId, so
  // removing a stale stub drops its connector by that key too.
  const connectorsByBlock = new Map<string, ExcalidrawElement>();
  for (const el of currentElements) {
    const mm = mindmapOf(el);
    if (mm?.role === "node") {
      currentNodes.set(mm.blockId, el);
    } else if (mm?.role === "connector") {
      connectorsByBlock.set(mm.blockId, el);
    }
  }

  const colorByBlock = assignBranchColors(freshTree);

  // parent → its fresh children (in reading order), so siblings of a new node
  // are the real tree siblings, not a geometric guess.
  const childrenByParent = new Map<string, string[]>();
  for (const { node, parentBlockId } of freshEntries) {
    if (parentBlockId !== null) {
      const list = childrenByParent.get(parentBlockId) ?? [];
      list.push(node.blockId);
      childrenByParent.set(parentBlockId, list);
    }
  }

  // Boxes we can anchor new nodes against: survivors keep their real geometry,
  // freshly-added nodes join the map as they are placed.
  const boxes = new Map<string, MindmapBox>();
  for (const [blockId, el] of currentNodes) {
    boxes.set(blockId, boxOf(el));
  }
  // The root anchors left/right growth; absent for a rootless diff scope.
  const rootBox = boxes.get(freshTree.blockId);

  // The colour each block actually WEARS, seeded from the canvas so an added
  // node inherits its parent's real branch colour rather than a positional one
  // recomputed from a tree whose branch order may have shifted (#R3). Filled in
  // for adds as they are placed, so a whole new subtree stays one colour.
  const wornColors = new Map<string, string>();
  for (const [blockId, el] of currentNodes) {
    const worn = branchColorOf(el);
    if (worn) {
      wornColors.set(blockId, worn);
    }
  }

  const renames: MindmapRename[] = [];
  const adds: MindmapAdd[] = [];
  const removes: string[] = [];
  const reparents: MindmapReparent[] = [];

  // Removes: a surviving-mapId node whose block is gone from the fresh tree,
  // plus its generated connector. User-drawn attachments carry no mapId, so
  // they are absent from currentElements and can never be removed here.
  for (const [blockId, el] of currentNodes) {
    if (!freshNodes.has(blockId) && !isTextNode(el)) {
      removes.push(el.id);
      const connector = connectorsByBlock.get(blockId);
      if (connector) {
        removes.push(connector.id);
      }
    }
  }

  // Text nodes are never removed; one whose parent is going moves to the
  // nearest ancestor that stays (a text ancestor always stays).
  const survives = (blockId: string) =>
    freshNodes.has(blockId) ||
    (currentNodes.has(blockId) && isTextNode(currentNodes.get(blockId)!));
  for (const [blockId, el] of currentNodes) {
    const parent = mindmapOf(el)?.parentBlockId;
    if (!isTextNode(el) || !parent || survives(parent)) {
      continue;
    }
    reparents.push({
      elementId: el.id,
      blockId,
      newParentBlockId: nearestSurvivingAncestor(
        blockId,
        currentNodes,
        survives,
        freshTree.blockId,
      ),
    });
  }

  // blockIds this pass has already accounted for. The fresh walk can legitimately
  // visit one block twice (a synced/transclusion block reachable from two
  // branches, or a block surfaced by both the root's tree and an expanded page's
  // tree), and without this guard the second visit found no live element for it —
  // the first visit had only planned an add, not committed one — so it planned a
  // SECOND add: the duplicate pill, once stacked on its twin and once shoved
  // clear by `avoidOverlaps`. A block live anywhere in the map is never re-added.
  const seenBlockIds = new Set<string>();

  for (const { node, parentBlockId } of freshEntries) {
    if (seenBlockIds.has(node.blockId)) {
      continue;
    }
    seenBlockIds.add(node.blockId);
    const existing = currentNodes.get(node.blockId);
    if (existing) {
      if (elementText(existing) !== node.text) {
        const box = renamedBox(existing, node);
        renames.push({
          elementId: existing.id,
          newText: node.text,
          ...(box ? { box } : {}),
        });
        if (box) {
          // Later placements collide against the box the pill will actually
          // occupy, not the one it is being resized out of.
          boxes.set(node.blockId, box);
        }
      }
      // A legacy v1 node carries no parentBlockId at all: there is nothing to
      // compare against, so it is never reparented.
      const recordedParent = mindmapOf(existing)?.parentBlockId;
      if (
        parentBlockId !== null &&
        recordedParent !== undefined &&
        recordedParent !== parentBlockId
      ) {
        reparents.push({
          elementId: existing.id,
          blockId: node.blockId,
          newParentBlockId: parentBlockId,
        });
      }
      continue;
    }
    if (parentBlockId === null) {
      // The root is always expected to survive; nothing sensible to anchor to.
      continue;
    }
    // The user deleted this node; Notion still lists it. Leave it deleted.
    if (excludeBlockIds?.has(node.blockId)) {
      continue;
    }

    const parentBox = boxes.get(parentBlockId);
    if (!parentBox) {
      continue;
    }

    const width = mindmapNodeWidth(node);
    const height = mindmapNodeHeight(node);
    const siblingBoxes = (childrenByParent.get(parentBlockId) ?? [])
      .filter((blockId) => blockId !== node.blockId)
      .map((blockId) => boxes.get(blockId))
      .filter((box): box is MindmapBox => box !== undefined);

    const side = sideOfBox(parentBox, rootBox);
    const box = avoidOverlaps(
      placeNear(parentBox, width, height, siblingBoxes, side),
      [...boxes.values(), ...(occupiedBoxes ?? [])],
    );
    boxes.set(node.blockId, box);

    // Inherit the parent's worn colour so an inserted branch never arrives in a
    // clashing palette colour; only a new branch under a NEUTRAL parent (the
    // root, or a spine node) falls back to the positional palette.
    const parentColor = wornColors.get(parentBlockId);
    const strokeColor =
      parentColor && parentColor !== MINDMAP_NEUTRAL_COLOR
        ? parentColor
        : colorByBlock.get(node.blockId);
    if (strokeColor) {
      wornColors.set(node.blockId, strokeColor);
    }

    adds.push({
      blockId: node.blockId,
      parentBlockId,
      node,
      box,
      parentBox,
      strokeColor,
      sourcePageId: sourceByBlockId?.get(node.blockId),
    });
  }

  const plan: MindmapPlan = { adds, removes, renames };
  if (reparents.length > 0) {
    plan.reparents = reparents;
  }
  return plan;
};

/* ------------------------------------------------------------------------- *
 * Whole-map rebuild
 *
 * The incremental placement above (`placeNear` + `avoidOverlaps`) positions each
 * new node against its parent and only ever pushes DOWN. That is right for a
 * handful of adds and catastrophic for a wholesale replacement: regrouping a
 * database rewrites EVERY row's blockId (a grouped row is keyed
 * `${groupId}::${rowId}`), so the diff sees 100% removes + 100% adds and the map
 * degenerates into one column thousands of pixels tall.
 *
 * A replacement is therefore not diffed at all: the tree is laid out from
 * scratch, exactly as generation does, and dropped back onto the canvas anchored
 * on the root's current position so the map does not jump.
 * ------------------------------------------------------------------------- */

/** A live node element of `mapId`, by blockId. */
const liveNode = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
  blockId: string,
): ExcalidrawElement | undefined =>
  scene.find((el) => {
    const mm = mindmapOf(el);
    return (
      !el.isDeleted &&
      mm?.mapId === mapId &&
      mm.role === "node" &&
      mm.blockId === blockId
    );
  });

/**
 * Whether a fresh DATABASE tree replaces the canvas outright rather than
 * amending it: nothing but the root survives, in either direction. Only a
 * regrouping produces that (every grouped blockId embeds its group), and the
 * incremental placement cannot express it. Page maps are excluded — their
 * blockIds do not move with a setting, so a total miss there is real churn in
 * Notion and the ordinary diff is still the honest answer.
 */
export const isMapReplacement = (
  fresh: MindmapTree,
  mine: readonly ExcalidrawElement[],
  rootPageId: string,
  source: MindmapSource,
): boolean => {
  if (source !== "database") {
    return false;
  }
  const canvas = new Set<string>();
  for (const el of mine) {
    const mm = mindmapOf(el);
    if (mm?.role === "node" && mm.blockId !== rootPageId) {
      canvas.add(mm.blockId);
    }
  }
  const freshEntries: FreshEntry[] = [];
  walkFresh(fresh, null, freshEntries);
  const freshIds = freshEntries
    .map((e) => e.node.blockId)
    .filter((blockId) => blockId !== rootPageId);
  if (canvas.size === 0 || freshIds.length === 0) {
    return false;
  }
  return !freshIds.some((blockId) => canvas.has(blockId));
};

/**
 * The row a database blockId stands for. A flat row is keyed by its page id and
 * a grouped one by `${groupId}::${rowId}`, so the trailing segment is the row's
 * identity under either grouping — the one thing about a database node that
 * survives a regroup.
 */
const rowIdentityOf = (blockId: string): string => {
  const separator = blockId.lastIndexOf("::");
  // Not `slice(lastIndexOf + 2)`: with no separator that is slice(1), which
  // silently returned a flat row's page id minus its first character.
  return separator === -1 ? blockId : blockId.slice(separator + 2);
};

/**
 * Drop the nodes a map's deletion ledger suppresses, matching on row identity so
 * a row deleted while the map was flat stays deleted once it is grouped. A group
 * left with no rows goes with them — an empty group branch is an artefact of the
 * deletion, not something Notion lists.
 */
const pruneSuppressed = (
  tree: MindmapTree,
  suppressed: ReadonlySet<string>,
): MindmapTree => {
  // BOTH sides through the same normalisation. Only the tree side was, so a row
  // deleted while GROUPED (ledger: "group::row") stayed deleted through another
  // regrouping but came back the moment the map went flat (tree: "row") — the
  // exact contract this function documents, half-enforced.
  const suppressedRows = new Set(
    [...suppressed].map((blockId) => rowIdentityOf(blockId)),
  );
  const children = tree.children
    .filter(
      (child) =>
        !suppressed.has(child.blockId) &&
        !suppressedRows.has(rowIdentityOf(child.blockId)),
    )
    .map((child) => pruneSuppressed(child, suppressed))
    .filter((child) => child.kind !== "block" || child.children.length > 0);
  return {
    ...tree,
    children,
    ...(tree.hasChildren !== undefined
      ? { hasChildren: tree.hasChildren && children.length > 0 }
      : {}),
  };
};

/**
 * Re-lay-out a whole map from a freshly built tree, returning the next scene —
 * or `null` when the map's root is no longer on canvas.
 *
 * The map keeps its `mapId`, its root's position (the layout is anchored on the
 * root's current centre) and the root's own bookkeeping: its deletion ledger,
 * its nudge flag and its stored grouping candidates all move to the new root
 * element. The old elements are tombstoned rather than dropped so one undo puts
 * them back and a collaborator's copy is told they are gone; they are stamped
 * `removedBy: "refresh"` because a rebuild is not a user deletion and must not
 * feed the resurrection exclusion.
 */
export const rebuildMapFromTree = (
  scene: readonly ExcalidrawElement[],
  ctx: {
    mapId: string;
    rootPageId: string;
    tree: MindmapTree;
    source?: MindmapSource;
    groupingProperty?: string;
    /** blockIds (or row identities) the map's ledger suppresses. */
    suppressed?: ReadonlySet<string>;
    /** The groupable properties to store on the rebuilt root. */
    groupingCandidates?: readonly Grouping[];
  },
): ExcalidrawElement[] | null => {
  const { mapId, rootPageId, source, groupingProperty } = ctx;
  const anchor = liveNode(scene, mapId, rootPageId);
  if (!anchor) {
    return null;
  }
  const anchorMm = mindmapOf(anchor)!;

  const pruned = ctx.suppressed?.size
    ? pruneSuppressed(ctx.tree, ctx.suppressed)
    : ctx.tree;
  // Text nodes are in no Notion-derived tree; ungrafted they would be retired.
  const tree = graftTextNodes(pruned, elementsForMap(scene, mapId));
  const emitted = emitMindmapElements(layoutMindmap(tree), tree, mapId, {
    source,
    groupingProperty,
  });
  const fresh = emitted.find(
    (el) =>
      mindmapOf(el)?.blockId === rootPageId && mindmapOf(el)?.role === "node",
  );
  if (!fresh) {
    return null;
  }

  const dx = anchor.x + anchor.width / 2 - (fresh.x + fresh.width / 2);
  const dy = anchor.y + anchor.height / 2 - (fresh.y + fresh.height / 2);
  const placed = emitted.map((el) => {
    const moved: ExcalidrawElement = { ...el, x: el.x + dx, y: el.y + dy };
    if (el.id !== fresh.id) {
      return moved;
    }
    const mm = mindmapOf(moved)!;
    return {
      ...moved,
      customData: {
        ...moved.customData,
        mindmap: {
          ...mm,
          ...(anchorMm.deletions ? { deletions: anchorMm.deletions } : {}),
          ...(anchorMm.deletedBlockIds
            ? { deletedBlockIds: anchorMm.deletedBlockIds }
            : {}),
          ...(anchorMm.largeFlatNudged ? { largeFlatNudged: true } : {}),
          ...(ctx.groupingCandidates ?? anchorMm.groupingCandidates
            ? {
                groupingCandidates: [
                  ...(ctx.groupingCandidates ?? anchorMm.groupingCandidates!),
                ],
              }
            : {}),
        },
      },
    };
  });

  // A grafted text node is re-emitted under its own id, so its old element is
  // replaced outright rather than retired: one id, one element.
  const placedIds = new Set(placed.map((el) => el.id));
  const retired = scene.flatMap((el) => {
    const mm = mindmapOf(el);
    if (el.isDeleted || mm?.mapId !== mapId) {
      return [el];
    }
    if (placedIds.has(el.id)) {
      return [];
    }
    return [
      newElementWith(el, {
        isDeleted: true,
        customData: {
          ...el.customData,
          mindmap: { ...mm, removedBy: "refresh" },
        },
      }),
    ];
  });

  // Same two sweeps every other apply path runs: keep this map's own pills off
  // each other, then shove any map it has grown into out of the way.
  return separateOverlappingMaps(
    resolveMapCollisions([...retired, ...placed], mapId),
    mapId,
  );
};

/**
 * Which side of the map a box grows on: the side it already sits on relative to
 * the root's center (mirrors `sideOf` in the expand pipeline). A left-hand
 * branch must keep growing left, otherwise refresh mirrors its new children
 * across the root and straight through the map. Without a root box (a rootless
 * diff scope) growth defaults right.
 */
const sideOfBox = (
  box: MindmapBox,
  rootBox: MindmapBox | undefined,
): 1 | -1 => {
  if (!rootBox) {
    return 1;
  }
  const rootCx = rootBox.x + rootBox.width / 2;
  return box.x + box.width / 2 >= rootCx ? 1 : -1;
};

const overlaps = (a: MindmapBox, b: MindmapBox): boolean =>
  a.x < b.x + b.width &&
  b.x < a.x + a.width &&
  a.y < b.y + b.height &&
  b.y < a.y + a.height;

/**
 * Push a freshly placed box straight down until it clears every box already on
 * the map (survivors plus adds placed earlier this pass). Refresh does no global
 * relayout, so without this an added branch lands on top of the existing map —
 * the stacked-pills bug. Monotonic in y over a finite set, so it terminates.
 */
const avoidOverlaps = (
  box: MindmapBox,
  occupied: Iterable<MindmapBox>,
): MindmapBox => {
  const others = [...occupied];
  let placed = box;
  let moved = true;
  while (moved) {
    moved = false;
    for (const other of others) {
      if (overlaps(placed, other)) {
        placed = { ...placed, y: other.y + other.height + V_GAP };
        moved = true;
      }
    }
  }
  return placed;
};

/**
 * Place a new node below the lowest sibling that already has a box; if the
 * parent has none yet, tuck it just off the parent's facing edge on `side`.
 * Siblings already placed this pass are included, so a batch of new children
 * stacks.
 */
const placeNear = (
  parentBox: MindmapBox,
  width: number,
  height: number,
  siblingBoxes: MindmapBox[],
  side: 1 | -1,
): MindmapBox => {
  if (siblingBoxes.length === 0) {
    return {
      x:
        side > 0
          ? parentBox.x + parentBox.width + H_GAP
          : parentBox.x - H_GAP - width,
      y: parentBox.y,
      width,
      height,
    };
  }
  const bottom = siblingBoxes.reduce(
    (max, s) => Math.max(max, s.y + s.height),
    -Infinity,
  );
  return {
    x: siblingBoxes[0].x,
    y: bottom + V_GAP,
    width,
    height,
  };
};

/**
 * The box a renamed pill should move to, or `undefined` to leave it alone.
 *
 * Auto-sized pills are re-fitted to the new label — the same width/height rules
 * generation and expand use — and stay centered where they are, so a title that
 * grew gains the second line the renderer would otherwise ellipsize away. A pill
 * whose box no longer matches what those rules produced for its OLD label was
 * resized by the user, and the canvas owns that: it is left untouched.
 */
const renamedBox = (
  el: ExcalidrawElement,
  fresh: MindmapTree,
): MindmapBox | undefined => {
  if (el.type !== "mindmapNode") {
    return undefined;
  }
  // The probe must describe the pill as it was LAID OUT — its shape is read off
  // the ELEMENT, not off `fresh`. A node that gains its first child (or an icon)
  // in the same refresh that renames it would otherwise be measured against a
  // size its box was never given, read as user-resized, and keep ellipsizing the
  // longer title until the user resized it by hand.
  const pill = el as ExcalidrawMindmapNodeElement;
  const was: MindmapTree = {
    ...fresh,
    text: elementText(el),
    kind: pill.nodeKind,
    icon: pill.icon,
    hasChildren: pill.expandable,
    truncated: false,
  };
  if (
    el.width !== mindmapNodeWidth(was) ||
    el.height !== mindmapNodeHeight(was)
  ) {
    return undefined;
  }
  const width = mindmapNodeWidth(fresh);
  const height = mindmapNodeHeight(fresh);
  if (width === el.width && height === el.height) {
    return undefined;
  }
  return {
    x: el.x + (el.width - width) / 2,
    y: el.y + (el.height - height) / 2,
    width,
    height,
  };
};

/**
 * Rewrite one node element's label to `newText`, keeping its canvas-owned style
 * (Notion owns text, the canvas owns geometry). A `mindmapNode` pill also takes
 * `box` when `renamedBox` decided the pill is still auto-sized. Legacy
 * `notion`/`text` nodes keep their original rename behavior.
 */
const renameElement = (
  el: ExcalidrawElement,
  newText: string,
  box?: MindmapBox,
): ExcalidrawElement => {
  if (el.type === "mindmapNode") {
    return newElementWith(el as ExcalidrawMindmapNodeElement, {
      label: newText,
      ...(box ?? {}),
    });
  }
  if (el.type === "notion") {
    return newElementWith(el as ExcalidrawNotionElement, { title: newText });
  }
  const textEl = el as ExcalidrawTextElement;
  return newElementWith(textEl, {
    text: newText,
    originalText: newText,
  });
};

/**
 * Apply a refresh plan to the scene's elements, returning the next element
 * array: renames rewritten in place, reparents rewiring a survivor to its new
 * parent (its `parentBlockId` and its incoming connector, nothing else),
 * removals marked `isDeleted` (never dropped, so one undo restores them), and
 * each add appended as a node element plus its connector. Pure — no scene
 * mutation, ready for a single committed `updateScene`.
 */
export const applyMindmapPlan = (
  currentElements: readonly ExcalidrawElement[],
  plan: MindmapPlan,
  mapId: string,
  rootPageId: string,
  mapMeta?: { source?: MindmapSource; groupingProperty?: string },
): ExcalidrawElement[] => {
  const identity: MindmapIdentity = {
    mapId,
    rootPageId,
    source: mapMeta?.source,
    groupingProperty: mapMeta?.groupingProperty,
  };
  const removeSet = new Set(plan.removes);
  const renameByElement = new Map(plan.renames.map((r) => [r.elementId, r]));
  const reparents = plan.reparents ?? [];
  const reparentByElement = new Map(reparents.map((r) => [r.elementId, r]));
  const reparentByBlock = new Map(reparents.map((r) => [r.blockId, r]));

  // Boxes for connector re-routing: survivors as they sit, adds as placed.
  const boxByBlock = new Map<string, MindmapBox>();
  // Each node block's current parent, so a re-fitted pill's incoming connector
  // can be re-anchored without waiting for a reparent to carry the parent id.
  const parentByBlock = new Map<string, string>();
  for (const el of currentElements) {
    const mm = mindmapOf(el);
    if (mm?.role === "node") {
      boxByBlock.set(mm.blockId, boxOf(el));
      if (mm.parentBlockId) {
        parentByBlock.set(mm.blockId, mm.parentBlockId);
      }
    }
  }
  for (const add of plan.adds) {
    boxByBlock.set(add.blockId, add.box);
  }
  // Blocks whose pill is being re-fitted to a longer/shorter label: their box
  // moves, so the connector arriving at it has to be re-routed like a reparent's.
  const reboxedBlocks = new Set<string>();
  for (const el of currentElements) {
    const box = renameByElement.get(el.id)?.box;
    const mm = mindmapOf(el);
    if (box && mm?.role === "node") {
      boxByBlock.set(mm.blockId, box);
      reboxedBlocks.add(mm.blockId);
    }
  }
  // Reparented blocks whose connector we found and re-routed; the rest get a
  // freshly built one below (a survivor may have lost its connector).
  const reroutedBlocks = new Set<string>();

  const next: ExcalidrawElement[] = currentElements.map((el) => {
    if (removeSet.has(el.id)) {
      // Stamp the provenance: THIS removal was Notion's, not the user's, so a
      // later refresh may bring the block back if Notion lists it again.
      const mm = mindmapOf(el);
      return newElementWith(el, {
        isDeleted: true,
        ...(mm
          ? {
              customData: {
                ...el.customData,
                mindmap: { ...mm, removedBy: "refresh" },
              },
            }
          : {}),
      });
    }
    let out = el;
    const rename = renameByElement.get(el.id);
    if (rename) {
      out = renameElement(out, rename.newText, rename.box);
    }
    const reparent = reparentByElement.get(el.id);
    if (reparent) {
      const mm = mindmapOf(out)!;
      out = newElementWith(out, {
        customData: {
          ...out.customData,
          mindmap: { ...mm, parentBlockId: reparent.newParentBlockId },
        },
      });
    }
    const mm = mindmapOf(out);
    if (
      mm?.role === "connector" &&
      (reparentByBlock.has(mm.blockId) || reboxedBlocks.has(mm.blockId))
    ) {
      const newParentBlockId =
        reparentByBlock.get(mm.blockId)?.newParentBlockId ??
        parentByBlock.get(mm.blockId);
      const parentBox = newParentBlockId
        ? boxByBlock.get(newParentBlockId)
        : undefined;
      const childBox = boxByBlock.get(mm.blockId);
      if (parentBox && childBox) {
        reroutedBlocks.add(mm.blockId);
        const { from, points } = mindmapConnectorGeometry(parentBox, childBox, {
          departRatio: mm.departRatio,
        });
        // Connectors are linear (arrow) elements, so `points` is a valid update.
        out = newElementWith(out as ExcalidrawLinearElement, {
          x: from.x,
          y: from.y,
          points,
        });
      }
    }
    return out;
  });

  for (const moved of reparents) {
    const parentBox = boxByBlock.get(moved.newParentBlockId);
    const childBox = boxByBlock.get(moved.blockId);
    if (reroutedBlocks.has(moved.blockId) || !parentBox || !childBox) {
      continue;
    }
    next.push(
      buildMindmapConnectorElement({
        identity,
        toBlockId: moved.blockId,
        fromBox: parentBox,
        toBox: childBox,
      }),
    );
  }

  for (const add of plan.adds) {
    next.push(
      buildMindmapNodeElement({
        identity,
        treeNode: add.node,
        box: add.box,
        branchColor: add.strokeColor ?? MINDMAP_NEUTRAL_COLOR,
        parentBlockId: add.parentBlockId,
        sourcePageId: add.sourcePageId,
      }),
    );
    next.push(
      buildMindmapConnectorElement({
        identity,
        toBlockId: add.blockId,
        fromBox: add.parentBox,
        toBox: add.box,
        strokeColor: add.strokeColor,
        stub: add.node.truncated,
      }),
    );
  }

  // `avoidOverlaps` places each add clear of the map as it stood, but a rename
  // re-fits a pill in place and a restored branch arrives whole, so the committed
  // scene is only guaranteed collision-free once it is checked as a whole — the
  // same sweep the expand path runs.
  const resolved = resolveMapCollisions(next, mapId);

  // `avoidOverlaps` only knows this map's boxes, so an add pushed down the
  // canvas can still land in a NEIGHBOURING map. Expand solved that by shoving
  // the collided map clear; refresh never did, which is why refreshed pills kept
  // appearing on top of another map's nodes. Same call, same rule.
  const separated = separateOverlappingMaps(resolved, mapId);

  // Bind the added / rewired arrows to their pills so they drag live, and repair
  // any pre-existing connector the map loaded unbound.
  return withMindmapBindings(separated);
};

/**
 * Block-level change counts for the refresh toast: added and renamed come
 * straight from the plan; removed counts node removals only, so the summary
 * reads "N removed" in blocks, not in elements (each removal also drops a
 * connector).
 */
export const summarizeRefresh = (
  plan: MindmapPlan,
  elements: readonly ExcalidrawElement[],
): { added: number; renamed: number; removed: number } => {
  const removeSet = new Set(plan.removes);
  const removed = elements.filter(
    (el) => removeSet.has(el.id) && mindmapOf(el)?.role === "node",
  ).length;
  return { added: plan.adds.length, renamed: plan.renames.length, removed };
};

/**
 * The distinct mapIds already mapping a given Notion page on this canvas,
 * recovered from `rootPageId` on any generated element. Used by the picker to
 * offer "Refresh existing" vs "New copy" when a page is generated again.
 */
export const existingMapIdsForPage = (
  elements: readonly ExcalidrawElement[],
  pageId: string,
): string[] => {
  const mapIds = new Set<string>();
  for (const el of elements) {
    const mm = mindmapOf(el);
    if (mm && mm.rootPageId === pageId) {
      mapIds.add(mm.mapId);
    }
  }
  return [...mapIds];
};

/**
 * Elements carrying a given mapId (both nodes and connectors). This is the
 * slice of the scene a refresh reconciles; everything else — including
 * user-drawn attachments to the map — is left strictly alone.
 */
const elementsForMap = (
  elements: readonly ExcalidrawElement[],
  mapId: string,
): ExcalidrawElement[] =>
  elements.filter((el) => mindmapOf(el)?.mapId === mapId);

/** The map's source, read from any generated element (legacy maps → "page"). */
const mapSourceOf = (mine: readonly ExcalidrawElement[]): MindmapSource => {
  for (const el of mine) {
    const source = mindmapOf(el)?.source;
    if (source) {
      return source;
    }
  }
  return "page";
};

/** The stored grouping property for a database map, if the map was grouped. */
const mapGroupingPropertyOf = (
  mine: readonly ExcalidrawElement[],
): string | undefined => {
  for (const el of mine) {
    const groupingProperty = mindmapOf(el)?.groupingProperty;
    if (groupingProperty) {
      return groupingProperty;
    }
  }
  return undefined;
};

/** The map root's current label, so a rebuilt tree keeps the same root text. */
const rootLabelOf = (
  mine: readonly ExcalidrawElement[],
  rootBlockId: string,
): string => {
  const root = mine.find((el) => mindmapOf(el)?.blockId === rootBlockId);
  return root ? elementText(root) : "Untitled";
};

/** Full + base (generation-shape) tree for one re-walked source page. */
interface SourceTrees {
  full: MindmapTree;
  base: MindmapTree;
}

/**
 * Non-root nodes this map currently shows. A refresh that would empty a
 * populated map is treated as a bad fetch rather than a real deletion (#R2).
 */
const visibleNodeCount = (
  mine: readonly ExcalidrawElement[],
  rootBlockId: string,
): number =>
  mine.filter((el) => {
    const mm = mindmapOf(el);
    return mm?.role === "node" && mm.blockId !== rootBlockId;
  }).length;

/**
 * Whether a freshly fetched root tree looks like a LOST source rather than an
 * emptied page. `fetchPageSource` only fails on a thrown error, an error field
 * or a missing body — a 200 whose recordMap lacks the root's blocks (a scoped-out
 * integration, a transient partial payload) builds a childless tree, and the diff
 * would dutifully remove every node on the canvas. Refuse to commit that.
 */
const looksLikeLostSource = (
  fresh: MindmapTree,
  canvasNodeCount: number,
): boolean => fresh.children.length === 0 && canvasNodeCount > 0;

/** Fetch a page's recordMap and derive its full + skeleton trees, or null. */
const fetchPageSource = async (pageId: string): Promise<SourceTrees | null> => {
  try {
    const res = await notionPageRecordMap({ pageId, publicUrl: null });
    if (res.error || !res.data) {
      return null;
    }
    const recordMap = res.data as ExtendedRecordMap;
    return {
      full: buildMindmapTree(recordMap, pageId),
      base: buildMindmapSkeleton(recordMap, pageId),
    };
  } catch {
    return null;
  }
};

/**
 * A database fetch either yields trees or one of two named failures: the query
 * itself failed, or the property the map was grouped by no longer resolves.
 */
type DatabaseSourceResult =
  | { trees: SourceTrees; groupingProperty?: string; candidates: Grouping[] }
  | "unreachable"
  | "groupingGone";

/**
 * Fetch a database's rows and build its tree (base === full: a database map's
 * generation shape IS root → groups → rows, so every level is always visible).
 * The root label is kept from the on-canvas map, since the query does not carry
 * the database's own title.
 */
const fetchDatabaseSource = async (
  databaseId: string,
  mine: readonly ExcalidrawElement[],
): Promise<DatabaseSourceResult> => {
  let rows: NotionDbRow[];
  try {
    const res = await notionDatabase(databaseId);
    if (res.error || !res.data?.results) {
      return "unreachable";
    }
    rows = res.data.results as NotionDbRow[];
  } catch {
    return "unreachable";
  }

  // Recomputed on every refresh: this is how a property added in Notion reaches
  // the Group by popover, which otherwise reads only what the root carries.
  const candidates = candidateGroupings(rows);
  const groupingProperty = mapGroupingPropertyOf(mine);
  const grouping = groupingProperty
    ? candidates.find((g) => g.propertyName === groupingProperty)
    : undefined;
  // A grouped map whose property vanished must NOT silently fall back to flat:
  // every group-scoped blockId would change, so the whole map would be removed
  // and re-added, losing every position and edit (#R8).
  if (groupingProperty && !grouping) {
    return "groupingGone";
  }

  const tree = buildDbMindmapTree(
    { id: databaseId, title: rootLabelOf(mine, databaseId) },
    rows,
    grouping,
  );
  return { trees: { full: tree, base: tree }, groupingProperty, candidates };
};

/** The map's collapsed remembered nodes whose source did NOT fail to fetch. */
const collectStaleRememberedIds = (
  mine: readonly ExcalidrawElement[],
  rootPageId: string,
  failedSources: ReadonlySet<string>,
): Set<string> => {
  const out = new Set<string>();
  for (const el of mine) {
    const mm = mindmapOf(el);
    if (
      mm?.role === "node" &&
      mm.remembered &&
      !failedSources.has(mm.sourcePageId ?? rootPageId)
    ) {
      out.add(el.id);
    }
  }
  return out;
};

/**
 * Flag each collapsed node's remembered subtree stale (its elements untouched),
 * so its next expand re-fetches instead of restoring possibly-stale content.
 */
const markRememberedStale = (
  elements: readonly ExcalidrawElement[],
  ids: ReadonlySet<string>,
): ExcalidrawElement[] =>
  elements.map((el) => {
    if (!ids.has(el.id)) {
      return el;
    }
    const mm = mindmapOf(el)!;
    return newElementWith(el, {
      customData: {
        ...el.customData,
        mindmap: { ...mm, rememberedStale: true },
      },
    });
  });

/** The refresh toast: the change summary, moved nodes, unreachable pages. */
const refreshToastMessage = (
  counts: { added: number; renamed: number; removed: number },
  moved: number,
  unreachable: number,
): string => {
  let summary =
    counts.added === 0 &&
    counts.renamed === 0 &&
    counts.removed === 0 &&
    moved === 0
      ? t("mindmap.refreshUpToDate")
      : t("mindmap.refreshSummary", counts);
  if (moved > 0) {
    // Moved nodes are counted separately: they are neither added nor removed,
    // and `summarizeRefresh`'s three-count shape is depended on elsewhere.
    const key =
      moved === 1 ? "mindmap.refreshMoved" : "mindmap.refreshMoved_plural";
    summary = `${summary} · ${t(key, { count: moved })}`;
  }
  if (unreachable > 0) {
    // The custom `t` does no automatic pluralization, so select the i18next
    // `_plural` key ourselves (base key = singular, count === 1).
    const key =
      unreachable === 1
        ? "mindmap.refreshUnreachablePartial"
        : "mindmap.refreshUnreachablePartial_plural";
    return `${summary} · ${t(key, { count: unreachable })}`;
  }
  return summary;
};

/**
 * The toast for a rebuilt map. A regroup replaces every node, so the refresh
 * summary's "54 added, 53 removed" describes nothing the user did — what they
 * asked for was a grouping, and that is what the toast reports.
 */
export const regroupToastMessage = (groupingProperty?: string): string =>
  groupingProperty
    ? t("mindmap.regroupedBy", { property: groupingProperty })
    : t("mindmap.regroupedFlat");

/**
 * Refresh a whole map from its Notion sources (issue #52). V2 refresh re-walks
 * EVERY expanded source — the root page (or database) plus every page a user
 * expanded in place — and diffs each at the depth that is actually visible on
 * canvas, reconciling all of them in ONE committed `updateScene` (one undo).
 *
 * Collapse-state is respected: an expanded subtree is re-walked; a chip-collapsed
 * node stays a leaf. A collapsed node's remembered subtree is not auto-updated —
 * it is marked stale so the next expand re-fetches. A source page that fails to
 * fetch keeps its subtree untouched and is reported in the toast; the root
 * failing leaves the whole map unchanged. Notion owns text + structure;
 * canvas-owned position/style and any element outside this mapId are never
 * touched.
 */
export const refreshMindmapFromNotion = async (
  api: ExcalidrawImperativeAPI,
  mapId: string,
  rootPageId: string,
): Promise<void> => {
  if (inFlightRefreshes.has(mapId)) {
    return;
  }
  inFlightRefreshes.add(mapId);
  // Feed the root pill's refresh button its spinner: the click had no visible
  // effect until the fetch came back, so nothing told the user it had landed.
  markRefreshInFlight(mapId);
  try {
    await runRefresh(api, mapId, rootPageId);
  } finally {
    inFlightRefreshes.delete(mapId);
    clearRefreshInFlight(mapId);
    // Refresh reads Notion uncached, so the expand cache now holds pre-refresh
    // copies of the same pages — an expand within its TTL would contradict the
    // map the user just refreshed.
    clearNotionRequestCache();
  }
};

/**
 * Maps with a refresh already running. Both refreshes would diff against the
 * same pre-fetch canvas and commit in turn, so the second would re-add every
 * node the first had just added (its adds were computed before they existed).
 * A second refresh of the same map is therefore a complete no-op; cleared in
 * `finally`. Mirrors `inFlightExpands` in the expand pipeline.
 */
const inFlightRefreshes = new Set<string>();

const runRefresh = async (
  api: ExcalidrawImperativeAPI,
  mapId: string,
  rootPageId: string,
): Promise<void> => {
  const mine = elementsForMap(api.getSceneElements(), mapId);
  const source = mapSourceOf(mine);
  const sourceIds = mindmapSourceIds(mine, rootPageId);
  const nodeCount = visibleNodeCount(mine, rootPageId);

  const fullTreeBySource = new Map<string, MindmapTree>();
  const baseTreeBySource = new Map<string, MindmapTree>();
  const failedSources = new Set<string>();
  let groupingProperty: string | undefined;
  let groupingCandidates: Grouping[] | undefined;

  // The ROOT source: a database query, or the root page's recordMap. If the
  // root is unreachable the whole map stays unchanged (nothing to anchor to).
  if (source === "database") {
    const dbSource = await fetchDatabaseSource(rootPageId, mine);
    if (dbSource === "groupingGone") {
      api.setToast({ message: t("mindmap.refreshGroupingGone") });
      return;
    }
    if (dbSource === "unreachable") {
      api.setToast({ message: t("mindmap.refreshUnreachable") });
      return;
    }
    if (looksLikeLostSource(dbSource.trees.full, nodeCount)) {
      api.setToast({ message: t("mindmap.refreshEmpty") });
      return;
    }
    fullTreeBySource.set(rootPageId, dbSource.trees.full);
    baseTreeBySource.set(rootPageId, dbSource.trees.base);
    groupingProperty = dbSource.groupingProperty;
    groupingCandidates = dbSource.candidates;
  } else {
    const pageSource = await fetchPageSource(rootPageId);
    if (!pageSource) {
      api.setToast({ message: t("mindmap.refreshUnreachable") });
      return;
    }
    if (looksLikeLostSource(pageSource.full, nodeCount)) {
      api.setToast({ message: t("mindmap.refreshEmpty") });
      return;
    }
    fullTreeBySource.set(rootPageId, pageSource.full);
    baseTreeBySource.set(rootPageId, pageSource.base);
  }

  // Every OTHER source is a page the user expanded in place — always a
  // recordMap. Fetched in parallel; results stay index-aligned with `others` so
  // the failed-source accounting is unchanged.
  const others = sourceIds.filter((sid) => sid !== rootPageId);
  const fetched = await Promise.all(others.map((sid) => fetchPageSource(sid)));
  others.forEach((sid, i) => {
    const pageSource = fetched[i];
    if (!pageSource) {
      failedSources.add(sid);
      return;
    }
    fullTreeBySource.set(sid, pageSource.full);
    baseTreeBySource.set(sid, pageSource.base);
  });

  // Re-read the LIVE scene after the last await: the canvas may have moved on
  // while the fetches ran (a drag, an expand, a drawn element), and committing
  // the plan against the pre-fetch snapshot would silently revert all of it.
  // Tombstones are read too, so the commit passes them through untouched (a
  // collapse's deliberately kept subtree must survive) and the diff knows which
  // blocks the user deleted.
  const live = api.getSceneElementsIncludingDeleted();
  const liveMine = elementsForMap(live, mapId).filter((el) => !el.isDeleted);
  const tombstoned = tombstonedBlockIds(live, mapId);

  commitMultiSourceRefresh(api, live, liveMine, {
    mapId,
    rootPageId,
    source,
    groupingProperty,
    groupingCandidates,
    fullTreeBySource,
    baseTreeBySource,
    failedSources,
    tombstoned,
  });
};

/**
 * blockIds of this map's tombstoned nodes that must STAY deleted — the diff's
 * resurrection exclusion.
 *
 * A tombstone stamped `removedBy: "refresh"` is skipped: refresh itself removed
 * that node because Notion no longer listed the block, so if Notion lists it
 * again the block is genuinely back and the map should show it. Every other
 * tombstone is a deliberate local hide — a user deletion, or a collapse's
 * remembered subtree — and resurrecting it would undo the user's own action.
 * An unstamped tombstone is treated as a user deletion: that is what every
 * tombstone written before the provenance stamp existed actually was.
 *
 * Tombstones alone are not enough, because they are stripped on every save: the
 * persisted deletion ledger on the map root is the durable source of truth, and
 * the live tombstones are unioned in so a deletion made in THIS session counts
 * even before the ledger write has landed. Anything live on canvas is subtracted
 * last — an undone deletion is not a deletion, and leaving it in would make the
 * restored pill disappear again on this very refresh.
 */
export const tombstonedBlockIds = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
): Set<string> => {
  const out = mindmapSuppressedBlockIds(scene, mapId);
  const live = new Set<string>();
  for (const el of scene) {
    const mm = mindmapOf(el);
    if (mm?.mapId !== mapId || mm.role !== "node") {
      continue;
    }
    if (!el.isDeleted) {
      live.add(mm.blockId);
    } else if (mm.removedBy !== "refresh") {
      out.add(mm.blockId);
    }
  }
  for (const blockId of live) {
    out.delete(blockId);
  }
  return out;
};

/**
 * Diff the combined comparison tree against the map's visible on-canvas elements
 * (failed-source subtrees excluded so they survive), apply the plan plus any
 * stale-remembered marks in ONE committed `updateScene`, and toast the totals.
 */
const commitMultiSourceRefresh = (
  api: ExcalidrawImperativeAPI,
  scene: readonly ExcalidrawElement[],
  mine: readonly ExcalidrawElement[],
  ctx: {
    mapId: string;
    rootPageId: string;
    source: MindmapSource;
    groupingProperty?: string;
    groupingCandidates?: readonly Grouping[];
    fullTreeBySource: ReadonlyMap<string, MindmapTree>;
    baseTreeBySource: ReadonlyMap<string, MindmapTree>;
    failedSources: ReadonlySet<string>;
    tombstoned: ReadonlySet<string>;
  },
): void => {
  const {
    mapId,
    rootPageId,
    source,
    groupingProperty,
    groupingCandidates,
    fullTreeBySource,
    baseTreeBySource,
    failedSources,
    tombstoned,
  } = ctx;

  // A failed source's nodes (and the connectors pointing at them) are dropped
  // from the diff scope, so the combined tree never diffs them and they survive.
  const failedBlockIds = new Set<string>();
  for (const el of mine) {
    const mm = mindmapOf(el);
    if (
      mm?.role === "node" &&
      failedSources.has(mm.sourcePageId ?? rootPageId)
    ) {
      failedBlockIds.add(mm.blockId);
    }
  }
  const diffScope = mine.filter((el) => {
    const mm = mindmapOf(el)!;
    return mm.role === "node"
      ? !failedSources.has(mm.sourcePageId ?? rootPageId)
      : !failedBlockIds.has(mm.blockId);
  });

  const { tree, sourceByBlockId } = buildCombinedComparisonTree({
    mine: diffScope,
    rootPageId,
    fullTreeBySource,
    baseTreeBySource,
    failedSources,
  });

  // Every live node of the map, including the ones the diff scope excluded, so a
  // freshly added pill never lands on a failed source's surviving subtree.
  const allLiveBoxes = mine
    .filter((el) => mindmapOf(el)?.role === "node")
    .map(boxOf);

  // Refresh is the reconciliation point for the deletion ledger: an id whose pill
  // is live again (the user undid the deletion) is dropped for good, so the
  // stored array tracks real deletions instead of accumulating stale ids.
  const pruned = pruneMindmapDeletionLedger(scene, mapId);
  const base = pruned ?? scene;

  // A regrouped database shares no blockId with the canvas, so there is nothing
  // to diff — lay the whole map out again around its root.
  if (isMapReplacement(tree, mine, rootPageId, source)) {
    const rebuilt = rebuildMapFromTree(base, {
      mapId,
      rootPageId,
      tree,
      source,
      groupingProperty,
      suppressed: tombstoned,
      groupingCandidates,
    });
    if (rebuilt) {
      api.updateScene({ elements: rebuilt, commitToHistory: true });
      api.setToast({ message: regroupToastMessage(groupingProperty) });
      return;
    }
  }

  const plan = diffMindmap(
    tree,
    diffScope,
    sourceByBlockId,
    tombstoned,
    allLiveBoxes,
  );
  const counts = summarizeRefresh(plan, diffScope);
  const moved = plan.reparents?.length ?? 0;
  const staleIds = collectStaleRememberedIds(mine, rootPageId, failedSources);

  const nothingChanged =
    counts.added === 0 &&
    counts.renamed === 0 &&
    counts.removed === 0 &&
    moved === 0;

  // The stored candidate list travels in this same commit, so a property added
  // in Notion reaches the popover as soon as the map is refreshed.
  const withCandidates = groupingCandidates
    ? withMindmapGroupingCandidates(base, mapId, groupingCandidates)
    : null;

  if (!nothingChanged || staleIds.size > 0 || pruned || withCandidates) {
    // Apply to the full scene so removes/renames land by id and new nodes append,
    // while every non-map element (and failed-source subtree) passes through.
    let next = nothingChanged
      ? withCandidates ?? base
      : applyMindmapPlan(withCandidates ?? base, plan, mapId, rootPageId, {
          source,
          groupingProperty,
        });
    if (staleIds.size > 0) {
      next = markRememberedStale(next, staleIds);
    }
    api.updateScene({ elements: next, commitToHistory: true });
  }

  api.setToast({
    message: refreshToastMessage(counts, moved, failedSources.size),
  });
};
